import { IGetSettingsResponse, IUpdateSettingsRequest } from 'shared';

import { orgHeader } from '../../../../common/lib/axiosBaseQuery';
import { emptySplitApi } from '../baseApi';
import { CacheTags } from '../../types/cacheTags';

export interface UpdateSettingsPayload {
  organisationId: string;
  settings: IUpdateSettingsRequest;
}

export interface IGetInboundAttachmentAddressResponse {
  address: string;
}

export interface IInboundAttachmentIssue {
  id: string;
  fileName: string;
  extractedInvoiceNumber: string | null;
  holdReason: string | null;
  fromAddress: string | null;
  receivedAt: string;
  canRetry: boolean;
}

export interface IGetInboundAttachmentIssuesResponse {
  issues: IInboundAttachmentIssue[];
}

export const settingsApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    fetchSettings: builder.query<IGetSettingsResponse, string>({
      query: organisationId => ({
        url: `settings`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      providesTags: [
        { type: CacheTags.Settings, id: `LIST` },
      ],
    }),
    fetchInboundAttachmentAddress: builder.query<IGetInboundAttachmentAddressResponse, string>({
      query: organisationId => ({
        url: `settings/inbound-attachment-address`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
    }),
    fetchInboundAttachmentIssues: builder.query<IGetInboundAttachmentIssuesResponse, string>({
      query: organisationId => ({
        url: `settings/inbound-attachment-issues`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
    }),
    retryInboundAttachment: builder.mutation<{ matched: boolean }, { organisationId: string; id: string }>({
      query: ({ organisationId, id }) => ({
        url: `settings/inbound-attachment-issues/${id}/retry`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
    }),
    updateSettings: builder.mutation<IGetSettingsResponse, UpdateSettingsPayload>({
      query: ({ organisationId, settings }) => ({
        url: `settings`,
        method: `PATCH`,
        headers: {
          [orgHeader]: organisationId,
        },
        data: settings,
      }),
      invalidatesTags: [
        { type: CacheTags.Settings, id: `LIST` },
      ],
    }),
    createSenderProfile: builder.mutation<void, string>({
      query: organisationId => ({
        url: `settings/senderProfile`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [
        { type: CacheTags.Settings, id: `LIST` },
      ],
    }),
    removeSenderProfile: builder.mutation<void, { organisationId: string; profileId: number }>({
      query: ({ organisationId, profileId }) => ({
        url: `settings/senderProfile/${profileId}`,
        method: `DELETE`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [
        { type: CacheTags.Settings, id: `LIST` },
      ],
    }),
  }),
});

export const {
  useFetchSettingsQuery,
  useFetchInboundAttachmentAddressQuery,
  useFetchInboundAttachmentIssuesQuery,
  useRetryInboundAttachmentMutation,
  useUpdateSettingsMutation,
  useCreateSenderProfileMutation,
  useRemoveSenderProfileMutation,
} = settingsApi;
